<template>
  <div class="space-y-6">
    <div class="flex justify-between items-center">
      <h2 class="text-2xl font-bold text-gray-900">Manage Holidays & Blocked Dates</h2>
      <div class="flex gap-2">
        <button
          @click="openScheduleChangeModal"
          class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          title="Change opening hours for a specific day"
        >
          <span>🕐</span>
          <span>Change Schedule</span>
        </button>
        <button
          @click="openSundayModal"
          class="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
          title="Open a Sunday for bookings"
        >
          <span>🔓</span>
          <span>Open Sunday</span>
        </button>
        <button
          @click="addCruiseShipWarning"
          class="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors flex items-center space-x-2"
          title="Quick add cruise ship warning"
        >
          <span>🚢</span>
          <span>Cruise Ship Warning</span>
        </button>
        <button
          @click="showAddModal = true"
          class="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors flex items-center space-x-2"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          <span>Add Holiday/Block</span>
        </button>
      </div>
    </div>

    <!-- Info Box -->
    <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <div class="flex items-start">
        <svg class="w-5 h-5 text-blue-400 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
        </svg>
        <div class="text-sm text-blue-800">
          <p class="font-medium">About Holiday Management</p>
          <p class="mt-1">Add custom holidays, private events, or blocked dates to prevent bookings. You can also add warnings (like cruise ship schedules) that show notices without blocking bookings.</p>
          <p class="mt-2 text-xs">💡 <strong>Quick tip:</strong> Use the "Cruise Ship Warning" button for common cruise ship notifications - it pre-fills common lunch times and a standard message.</p>
          <p class="mt-2 text-xs">🕐 <strong>Change Schedule:</strong> Use the "Change Schedule" button to change the opening hours for a specific day (e.g., open earlier on a Saturday).</p>
          <p class="mt-2 text-xs">🔓 <strong>Open Sunday:</strong> Use the "Open Sunday" button to unlock a specific Sunday for bookings with custom hours and cruise ship warnings.</p>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-12">
      <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      <p class="mt-4 text-gray-600">Loading holidays...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="p-4 bg-red-50 border border-red-200 rounded-lg">
      <p class="text-red-800">{{ error }}</p>
    </div>

    <!-- Holidays List -->
    <div v-else class="space-y-4">
      <!-- Custom Holidays -->
      <div class="bg-white border rounded-lg overflow-hidden">
        <div class="bg-gray-50 px-6 py-3 border-b">
          <h3 class="font-semibold text-gray-900">Custom Holidays & Blocked Dates</h3>
        </div>
        <template v-for="group in holidayGroups" :key="group.label">
          <div class="bg-gray-50 px-6 py-2 border-b text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {{ group.label }}
          </div>
          <div class="divide-y">
          <div
            v-for="holiday in group.holidays"
            :key="holiday.date"
            :class="[
              'px-6 py-4 flex justify-between items-center hover:bg-gray-50',
              group.past ? 'opacity-60' : ''
            ]"
          >
            <div class="flex-grow">
              <div class="flex items-center space-x-3">
                <span v-if="holiday.actionType === 'schedule_change'" class="text-blue-500" title="Schedule Change">
                  🕐
                </span>
                <span v-else-if="holiday.actionType === 'sunday_open'" class="text-green-500" title="Sunday Open">
                  🔓
                </span>
                <span v-else-if="holiday.actionType === 'warning'" class="text-yellow-500" title="Warning">
                  ⚠️
                </span>
                <span v-else class="text-red-500" title="Blocked">
                  🚫
                </span>
                <p class="font-semibold text-gray-900">{{ holiday.name }}</p>
                <span
                  :class="[
                    'px-2 py-1 text-xs rounded-full font-medium',
                    holiday.actionType === 'schedule_change' ? 'bg-blue-100 text-blue-800' :
                    holiday.actionType === 'sunday_open' ? 'bg-green-100 text-green-800' :
                    holiday.actionType === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                    holiday.type === 'private_event' ? 'bg-purple-100 text-purple-800' :
                    holiday.type === 'holiday' ? 'bg-red-100 text-red-800' :
                    holiday.type === 'maintenance' ? 'bg-orange-100 text-orange-800' :
                    'bg-gray-100 text-gray-800'
                  ]"
                >
                  {{ holiday.actionType === 'schedule_change' ? 'Schedule Change' : holiday.actionType === 'sunday_open' ? 'Sunday Open' : holiday.actionType === 'warning' ? 'Warning' : holiday.type }}
                </span>
              </div>
              <p class="text-sm text-gray-500 mt-1">
                {{ formatDate(holiday.date) }}
                <span v-if="(holiday.actionType === 'sunday_open' || holiday.actionType === 'schedule_change') && holiday.availableTimeSlots" class="ml-2">
                  (Available: {{ holiday.availableTimeSlots.join(', ') }})
                </span>
                <span v-else-if="holiday.blockedTimeSlots || holiday.warningTimeSlots" class="ml-2">
                  (Time slots: {{ (holiday.blockedTimeSlots || holiday.warningTimeSlots || []).join(', ') }})
                </span>
                <span v-else-if="holiday.actionType === 'block'" class="ml-2">(Entire day blocked)</span>
              </p>
              <p v-if="holiday.actionType === 'schedule_change' && holiday.openingTime" class="text-sm text-blue-700 mt-1">
                Opening at: {{ holiday.openingTime }}
              </p>
              <p v-if="holiday.actionType === 'sunday_open' && holiday.openingTime" class="text-sm text-green-700 mt-1">
                Opening at: {{ holiday.openingTime }}
              </p>
              <p v-if="holiday.warningMessage" class="text-sm text-yellow-700 mt-1 italic">
                "{{ holiday.warningMessage }}"
              </p>
              <div v-if="holiday.cruiseShips && holiday.cruiseShips.length" class="mt-2 text-sm text-gray-600 space-y-1">
                <p
                  v-for="cruiseShip in holiday.cruiseShips"
                  :key="`${holiday.date}-${cruiseShip.ship}-${cruiseShip.portTime}`"
                >
                  <span class="font-medium">{{ cruiseShip.ship }}</span>
                  <span v-if="cruiseShip.portTime"> · {{ cruiseShip.portTime }}</span>
                  <span v-if="cruiseShip.estimatedPassengers"> · {{ formatPassengerCount(cruiseShip.estimatedPassengers) }} est. passengers</span>
                </p>
                <p v-if="holiday.totalEstimatedPassengers && holiday.cruiseShips.length > 1" class="text-xs text-gray-500">
                  Total estimated passengers: {{ formatPassengerCount(holiday.totalEstimatedPassengers) }}
                </p>
              </div>
            </div>
            <button
              @click="startEditHoliday(holiday)"
              class="text-gray-500 hover:text-black ml-4"
              title="Edit"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              @click="deleteHoliday(holiday.date)"
              class="text-red-600 hover:text-red-800 ml-2"
              title="Delete"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
          <div v-if="group.holidays.length === 0" class="px-6 py-8 text-center text-gray-500">
            {{ group.emptyText }}
          </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Add Holiday Modal -->
    <div
      v-if="showAddModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click.self="closeAddModal"
    >
      <div class="bg-white rounded-lg p-6 max-w-md w-full">
        <div class="flex justify-between items-start mb-4">
          <div>
            <h3 class="text-xl font-bold">
              {{ editingHoliday ? 'Edit Holiday/Blocked Date' : newHoliday.actionType === 'warning' && newHoliday.name === 'Cruise Ship Schedule' ? 'Add Cruise Ship Warning' : 'Add Holiday/Blocked Date' }}
            </h3>
            <p v-if="newHoliday.actionType === 'warning' && newHoliday.name === 'Cruise Ship Schedule'" class="text-sm text-gray-500 mt-1">
              Quick template - adjust date, times, and message as needed
            </p>
          </div>
          <button @click="closeAddModal" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form @submit.prevent="addHoliday" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              v-model="newHoliday.date"
              type="date"
              required
              :disabled="editingHoliday"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Name/Reason</label>
            <input
              v-model="newHoliday.name"
              type="text"
              required
              placeholder="e.g., Private Event, Maintenance Day"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              v-model="newHoliday.type"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
            >
              <option value="custom">Custom</option>
              <option value="holiday">Public Holiday</option>
              <option value="cruise_ship">Cruise Ship</option>
              <option value="private_event">Private Event</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Action Type</label>
            <select
              v-model="newHoliday.actionType"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
            >
              <option value="block">Block bookings (prevent booking)</option>
              <option value="warning">Show warning (allow booking with notice)</option>
            </select>
          </div>

          <div v-if="newHoliday.actionType === 'warning'">
            <label class="block text-sm font-medium text-gray-700 mb-1">Warning Message</label>
            <textarea
              v-model="newHoliday.warningMessage"
              rows="2"
              placeholder="e.g., Cruise ship groups scheduling during these times"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
            ></textarea>
          </div>

          <div v-if="newHoliday.type === 'cruise_ship'" class="space-y-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div>
              <label class="block text-sm font-medium text-yellow-900 mb-1">Ship</label>
              <input
                v-model="newHoliday.ship"
                type="text"
                placeholder="e.g., Quantum OTS"
                class="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-yellow-900 mb-1">Port Vila Time</label>
              <input
                v-model="newHoliday.portTime"
                type="text"
                placeholder="e.g., 7:00am-5:00pm"
                class="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-yellow-900 mb-1">Estimated Passengers</label>
              <input
                v-model.number="newHoliday.estimatedPassengers"
                type="number"
                min="0"
                step="1"
                placeholder="e.g., 4180"
                class="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              />
            </div>
          </div>

          <div v-if="newHoliday.actionType === 'block'">
            <label class="flex items-center space-x-2">
              <input
                v-model="newHoliday.blockEntireDay"
                type="checkbox"
                class="rounded border-gray-300 text-black focus:ring-gold"
              />
              <span class="text-sm font-medium text-gray-700">Block entire day</span>
            </label>
          </div>

          <div v-if="(newHoliday.actionType === 'block' && !newHoliday.blockEntireDay) || newHoliday.actionType === 'warning'">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ newHoliday.actionType === 'warning' ? 'Time Slots for Warning' : 'Blocked Time Slots' }}
            </label>
            <p class="text-xs text-gray-500 mb-2">
              {{ newHoliday.actionType === 'warning' ? 'Select times to show warning' : 'Select specific times to block (optional)' }}
            </p>
            <div class="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
              <label
                v-for="time in availableTimeSlots"
                :key="time"
                class="flex items-center space-x-2 text-sm"
              >
                <input
                  v-model="newHoliday.timeSlots"
                  :value="time"
                  type="checkbox"
                  class="rounded border-gray-300 text-black focus:ring-gold"
                />
                <span>{{ time }}</span>
              </label>
            </div>
          </div>

          <div v-if="addError" class="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p class="text-sm text-red-800">{{ addError }}</p>
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              @click="closeAddModal"
              class="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="adding"
              class="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {{ adding ? 'Saving...' : editingHoliday ? 'Save Changes' : 'Add Holiday' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Change Schedule Modal -->
    <div
      v-if="showScheduleChangeModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click.self="closeScheduleChangeModal"
    >
      <div class="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div class="flex justify-between items-start mb-4">
          <div>
            <h3 class="text-xl font-bold text-blue-700">{{ editingScheduleChange ? 'Edit Schedule Change' : 'Change Schedule for a Day' }}</h3>
            <p class="text-sm text-gray-500 mt-1">
              Override the regular opening hours for a specific date
            </p>
          </div>
          <button @click="closeScheduleChangeModal" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form @submit.prevent="addScheduleChange" class="space-y-4">
          <!-- Date Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              v-model="scheduleChange.date"
              type="date"
              required
              :min="editingScheduleChange ? undefined : todayStr"
              :disabled="editingScheduleChange"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
            />
          </div>

          <!-- Reason -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <input
              v-model="scheduleChange.name"
              type="text"
              required
              placeholder="e.g., Early Saturday opening, Extended hours"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <!-- Opening Time -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Opening Time</label>
            <select
              v-model="scheduleChange.openingTime"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="8:00 AM">8:00 AM</option>
              <option value="9:00 AM">9:00 AM</option>
              <option value="10:00 AM">10:00 AM</option>
              <option value="11:00 AM">11:00 AM</option>
              <option value="12:00 PM">12:00 PM (Noon)</option>
            </select>
          </div>

          <!-- Available Time Slots -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Available Booking Time Slots</label>
            <p class="text-xs text-gray-500 mb-2">Select which time slots will be available for bookings</p>
            <div class="grid grid-cols-2 gap-2">
              <label
                v-for="time in availableTimeSlots"
                :key="'sched-' + time"
                class="flex items-center space-x-2 text-sm"
              >
                <input
                  v-model="scheduleChange.availableTimeSlots"
                  :value="time"
                  type="checkbox"
                  class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>{{ time }}</span>
              </label>
            </div>
            <button
              type="button"
              @click="selectAllScheduleChangeSlots"
              class="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
            >
              Select All
            </button>
          </div>

          <div v-if="scheduleChangeError" class="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p class="text-sm text-red-800">{{ scheduleChangeError }}</p>
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              @click="closeScheduleChangeModal"
              class="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="addingScheduleChange || scheduleChange.availableTimeSlots.length === 0"
              class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {{ addingScheduleChange ? 'Saving...' : editingScheduleChange ? 'Save Changes' : 'Save Schedule Change' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Open Sunday Modal -->
    <div
      v-if="showSundayModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click.self="closeSundayModal"
    >
      <div class="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div class="flex justify-between items-start mb-4">
          <div>
            <h3 class="text-xl font-bold text-green-700">Open Sunday for Bookings</h3>
            <p class="text-sm text-gray-500 mt-1">
              Unlock a Sunday to accept bookings with custom hours
            </p>
          </div>
          <button @click="closeSundayModal" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form @submit.prevent="addSundayOpening" class="space-y-4">
          <!-- Date Selection (Sundays only) -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Select Sunday</label>
            <select
              v-model="sundayOpening.date"
              required
              :disabled="editingSunday"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
            >
              <option value="">Select a Sunday...</option>
              <option v-if="editingSunday && sundayOpening.date" :value="sundayOpening.date">
                {{ formatDate(sundayOpening.date) }}
              </option>
              <option v-for="sunday in upcomingSundays" :key="sunday.date" :value="sunday.date">
                {{ sunday.label }}
              </option>
            </select>
          </div>

          <!-- Event Name -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Event/Reason Name</label>
            <input
              v-model="sundayOpening.name"
              type="text"
              required
              placeholder="e.g., Cruise Ship Sunday, Special Event"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          <!-- Opening Time -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Opening Time</label>
            <select
              v-model="sundayOpening.openingTime"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="8:00 AM">8:00 AM</option>
              <option value="9:00 AM">9:00 AM</option>
              <option value="10:00 AM">10:00 AM</option>
              <option value="11:00 AM">11:00 AM</option>
              <option value="12:00 PM">12:00 PM (Noon)</option>
            </select>
          </div>

          <!-- Available Time Slots -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Available Booking Time Slots</label>
            <p class="text-xs text-gray-500 mb-2">Select which time slots will be available for bookings</p>
            <div class="grid grid-cols-2 gap-2">
              <label
                v-for="time in availableTimeSlots"
                :key="time"
                class="flex items-center space-x-2 text-sm"
              >
                <input
                  v-model="sundayOpening.availableTimeSlots"
                  :value="time"
                  type="checkbox"
                  class="rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span>{{ time }}</span>
              </label>
            </div>
            <button
              type="button"
              @click="selectAllTimeSlots"
              class="mt-2 text-xs text-green-600 hover:text-green-800 underline"
            >
              Select All
            </button>
          </div>

          <!-- Cruise Ship Warning Option -->
          <div class="border-t pt-4 mt-4">
            <label class="flex items-center space-x-2">
              <input
                v-model="sundayOpening.hasCruiseWarning"
                type="checkbox"
                class="rounded border-gray-300 text-yellow-500 focus:ring-yellow-400"
              />
              <span class="text-sm font-medium text-gray-700">Add cruise ship warning to specific times</span>
            </label>
          </div>

          <!-- Cruise Ship Warning Times -->
          <div v-if="sundayOpening.hasCruiseWarning" class="space-y-3 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <label class="block text-sm font-medium text-yellow-800 mb-1">Times with Cruise Ship Warning</label>
            <p class="text-xs text-yellow-700 mb-2">Select times to show a warning about potential cruise ship groups</p>
            <div class="grid grid-cols-2 gap-2">
              <label
                v-for="time in sundayOpening.availableTimeSlots"
                :key="'warning-' + time"
                class="flex items-center space-x-2 text-sm"
              >
                <input
                  v-model="sundayOpening.warningTimeSlots"
                  :value="time"
                  type="checkbox"
                  class="rounded border-yellow-400 text-yellow-500 focus:ring-yellow-400"
                />
                <span>{{ time }}</span>
              </label>
            </div>
            <div class="mt-3">
              <label class="block text-sm font-medium text-yellow-800 mb-1">Warning Message</label>
              <textarea
                v-model="sundayOpening.warningMessage"
                rows="2"
                placeholder="e.g., Cruise ship groups may be scheduling during these times"
                class="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              ></textarea>
            </div>
          </div>

          <div v-if="sundayError" class="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p class="text-sm text-red-800">{{ sundayError }}</p>
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              @click="closeSundayModal"
              class="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="addingSunday || sundayOpening.availableTimeSlots.length === 0"
              class="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {{ addingSunday ? 'Saving...' : editingSunday ? 'Save Changes' : 'Open Sunday' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { format, parseISO, addDays, getDay, startOfDay } from 'date-fns'
import { useAuth } from '../../composables/useAuth'
import axios from 'axios'

const { getAuthAxios } = useAuth()
const holidays = ref([])
const loading = ref(false)
const error = ref(null)
const showAddModal = ref(false)
const adding = ref(false)
const addError = ref(null)

// Edit mode flags (per modal); saving uses PATCH instead of POST
const editingHoliday = ref(false)
const editingScheduleChange = ref(false)
const editingSunday = ref(false)

// Schedule change modal state
const showScheduleChangeModal = ref(false)
const addingScheduleChange = ref(false)
const scheduleChangeError = ref(null)
const scheduleChange = ref({
  date: '',
  name: '',
  openingTime: '8:00 AM',
  availableTimeSlots: []
})

const todayStr = computed(() => {
  const d = new Date()
  return d.toISOString().split('T')[0]
})

// Sunday opening modal state
const showSundayModal = ref(false)
const addingSunday = ref(false)
const sundayError = ref(null)

const availableTimeSlots = [
  '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
  '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'
]

const newHoliday = ref({
  date: '',
  name: '',
  type: 'custom',
  actionType: 'block',
  blockEntireDay: true,
  timeSlots: [],
  warningMessage: '',
  ship: '',
  portTime: '',
  estimatedPassengers: null
})

// Sunday opening form state
const sundayOpening = ref({
  date: '',
  name: '',
  openingTime: '8:00 AM',
  availableTimeSlots: [],
  hasCruiseWarning: false,
  warningTimeSlots: [],
  warningMessage: 'Cruise ship groups may be scheduling during these times'
})

// Computed: Get upcoming Sundays for the next 3 months
const upcomingSundays = computed(() => {
  const sundays = []
  const today = startOfDay(new Date())
  let current = today

  // Find next Sunday
  while (getDay(current) !== 0) {
    current = addDays(current, 1)
  }

  // Get Sundays for next 3 months (roughly 13 weeks)
  for (let i = 0; i < 13; i++) {
    const sundayDate = addDays(current, i * 7)
    const dateStr = format(sundayDate, 'yyyy-MM-dd')

    // Check if this Sunday is already in holidays (either blocked or opened)
    const existingHoliday = holidays.value.find(h => h.date === dateStr)
    if (!existingHoliday) {
      sundays.push({
        date: dateStr,
        label: format(sundayDate, 'EEEE, MMMM d, yyyy')
      })
    }
  }

  return sundays
})

const holidayGroups = computed(() => {
  const today = format(new Date(), 'yyyy-MM-dd')
  const upcoming = holidays.value
    .filter(h => h.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date))
  const past = holidays.value
    .filter(h => h.date < today)
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 5)

  const groups = [{
    label: 'Upcoming',
    past: false,
    holidays: upcoming,
    emptyText: 'No upcoming holidays or blocked dates'
  }]
  if (past.length > 0) {
    groups.push({
      label: 'Past (last 5)',
      past: true,
      holidays: past,
      emptyText: ''
    })
  }
  return groups
})

const fetchHolidays = async () => {
  loading.value = true
  error.value = null

  try {
    // Fetch custom holidays (public endpoint, no auth needed)
    const response = await axios.get('/api/admin-holidays')

    if (response.data.success) {
      holidays.value = response.data.holidays
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to load holidays'
    console.error('Error fetching holidays:', err)
  } finally {
    loading.value = false
  }
}

const addHoliday = async () => {
  adding.value = true
  addError.value = null

  try {
    const authAxios = getAuthAxios()

    const payload = {
      date: newHoliday.value.date,
      name: newHoliday.value.name,
      type: newHoliday.value.type,
      actionType: newHoliday.value.actionType,
      blockedTimeSlots: newHoliday.value.actionType === 'block' ?
        (newHoliday.value.blockEntireDay ? null : newHoliday.value.timeSlots) :
        null,
      warningTimeSlots: newHoliday.value.actionType === 'warning' ? newHoliday.value.timeSlots : null,
      warningMessage: newHoliday.value.actionType === 'warning' ? newHoliday.value.warningMessage : null,
      cruiseShips: newHoliday.value.type === 'cruise_ship' && newHoliday.value.ship
        ? [{
            ship: newHoliday.value.ship,
            portTime: newHoliday.value.portTime,
            estimatedPassengers: newHoliday.value.estimatedPassengers
          }]
        : null
    }

    const response = editingHoliday.value
      ? await authAxios.patch('/api/admin-holidays', payload)
      : await authAxios.post('/api/admin-holidays', payload)

    if (response.data.success) {
      replaceHoliday(response.data.holiday)
      closeAddModal()
    }
  } catch (err) {
    addError.value = err.response?.data?.error || 'Failed to add holiday'
    console.error('Error adding holiday:', err)
  } finally {
    adding.value = false
  }
}

const deleteHoliday = async (date) => {
  if (!confirm('Are you sure you want to remove this holiday?')) return

  try {
    const authAxios = getAuthAxios()
    const response = await authAxios.delete(`/api/admin-holidays?date=${date}`)

    if (response.data.success) {
      holidays.value = holidays.value.filter(h => h.date !== date)
    }
  } catch (err) {
    alert(err.response?.data?.error || 'Failed to delete holiday')
    console.error('Error deleting holiday:', err)
  }
}

const formatDate = (dateStr) => {
  try {
    return format(parseISO(dateStr), 'EEEE, MMMM d, yyyy')
  } catch {
    return dateStr
  }
}

const formatPassengerCount = (count) => {
  return Number(count).toLocaleString()
}

const replaceHoliday = (holiday) => {
  const existingIndex = holidays.value.findIndex(h => h.date === holiday.date)
  if (existingIndex >= 0) {
    holidays.value.splice(existingIndex, 1, holiday)
  } else {
    holidays.value.push(holiday)
  }
}

const startEditHoliday = (holiday) => {
  if (holiday.actionType === 'schedule_change') {
    editingScheduleChange.value = true
    scheduleChange.value = {
      date: holiday.date,
      name: holiday.name,
      openingTime: holiday.openingTime || '8:00 AM',
      availableTimeSlots: [...(holiday.availableTimeSlots || [])]
    }
    scheduleChangeError.value = null
    showScheduleChangeModal.value = true
    return
  }

  if (holiday.actionType === 'sunday_open') {
    editingSunday.value = true
    sundayOpening.value = {
      date: holiday.date,
      name: holiday.name,
      openingTime: holiday.openingTime || '8:00 AM',
      availableTimeSlots: [...(holiday.availableTimeSlots || [])],
      hasCruiseWarning: !!(holiday.warningTimeSlots && holiday.warningTimeSlots.length),
      warningTimeSlots: [...(holiday.warningTimeSlots || [])],
      warningMessage: holiday.warningMessage || 'Cruise ship groups may be scheduling during these times'
    }
    sundayError.value = null
    showSundayModal.value = true
    return
  }

  // block / warning
  editingHoliday.value = true
  newHoliday.value = {
    date: holiday.date,
    name: holiday.name,
    type: holiday.type || 'custom',
    actionType: holiday.actionType || 'block',
    blockEntireDay: (holiday.actionType || 'block') === 'block' && (!holiday.blockedTimeSlots || holiday.blockedTimeSlots.length === 0),
    timeSlots: [...(holiday.blockedTimeSlots || holiday.warningTimeSlots || [])],
    warningMessage: holiday.warningMessage || '',
    ship: holiday.cruiseShips?.[0]?.ship || '',
    portTime: holiday.cruiseShips?.[0]?.portTime || '',
    estimatedPassengers: holiday.cruiseShips?.[0]?.estimatedPassengers ?? null
  }
  addError.value = null
  showAddModal.value = true
}

const addCruiseShipWarning = () => {
  editingHoliday.value = false
  // Pre-fill form with cruise ship warning defaults
  newHoliday.value = {
    date: '',
    name: 'Cruise Ship Schedule',
    type: 'cruise_ship',
    actionType: 'warning',
    blockEntireDay: false,
    timeSlots: ['12:00 PM', '1:00 PM', '2:00 PM'], // Common lunch times
    warningMessage: 'Cruise ship groups may be scheduling during these times',
    ship: '',
    portTime: '',
    estimatedPassengers: null
  }
  showAddModal.value = true
}

const closeAddModal = () => {
  showAddModal.value = false
  addError.value = null
  editingHoliday.value = false
  newHoliday.value = {
    date: '',
    name: '',
    type: 'custom',
    actionType: 'block',
    blockEntireDay: true,
    timeSlots: [],
    warningMessage: '',
    ship: '',
    portTime: '',
    estimatedPassengers: null
  }
}

// Schedule change functions
const openScheduleChangeModal = () => {
  editingScheduleChange.value = false
  scheduleChange.value = {
    date: '',
    name: '',
    openingTime: '8:00 AM',
    availableTimeSlots: []
  }
  scheduleChangeError.value = null
  showScheduleChangeModal.value = true
}

const closeScheduleChangeModal = () => {
  showScheduleChangeModal.value = false
  scheduleChangeError.value = null
  editingScheduleChange.value = false
}

const selectAllScheduleChangeSlots = () => {
  scheduleChange.value.availableTimeSlots = [...availableTimeSlots]
}

const addScheduleChange = async () => {
  addingScheduleChange.value = true
  scheduleChangeError.value = null

  try {
    const authAxios = getAuthAxios()

    const payload = {
      date: scheduleChange.value.date,
      name: scheduleChange.value.name,
      type: 'schedule_change',
      actionType: 'schedule_change',
      openingTime: scheduleChange.value.openingTime,
      availableTimeSlots: scheduleChange.value.availableTimeSlots
    }

    const response = editingScheduleChange.value
      ? await authAxios.patch('/api/admin-holidays', payload)
      : await authAxios.post('/api/admin-holidays', payload)

    if (response.data.success) {
      replaceHoliday(response.data.holiday)
      closeScheduleChangeModal()
    }
  } catch (err) {
    scheduleChangeError.value = err.response?.data?.error || 'Failed to save schedule change'
    console.error('Error saving schedule change:', err)
  } finally {
    addingScheduleChange.value = false
  }
}

// Sunday opening functions
const openSundayModal = () => {
  editingSunday.value = false
  sundayOpening.value = {
    date: '',
    name: '',
    openingTime: '8:00 AM',
    availableTimeSlots: [],
    hasCruiseWarning: false,
    warningTimeSlots: [],
    warningMessage: 'Cruise ship groups may be scheduling during these times'
  }
  sundayError.value = null
  showSundayModal.value = true
}

const closeSundayModal = () => {
  showSundayModal.value = false
  sundayError.value = null
  editingSunday.value = false
}

const selectAllTimeSlots = () => {
  sundayOpening.value.availableTimeSlots = [...availableTimeSlots]
}

const addSundayOpening = async () => {
  addingSunday.value = true
  sundayError.value = null

  try {
    const authAxios = getAuthAxios()

    const payload = {
      date: sundayOpening.value.date,
      name: sundayOpening.value.name,
      type: 'sunday_open',
      actionType: 'sunday_open',
      openingTime: sundayOpening.value.openingTime,
      availableTimeSlots: sundayOpening.value.availableTimeSlots,
      warningTimeSlots: sundayOpening.value.hasCruiseWarning ? sundayOpening.value.warningTimeSlots : null,
      warningMessage: sundayOpening.value.hasCruiseWarning ? sundayOpening.value.warningMessage : null
    }

    const response = editingSunday.value
      ? await authAxios.patch('/api/admin-holidays', payload)
      : await authAxios.post('/api/admin-holidays', payload)

    if (response.data.success) {
      replaceHoliday(response.data.holiday)
      closeSundayModal()
    }
  } catch (err) {
    sundayError.value = err.response?.data?.error || 'Failed to open Sunday for bookings'
    console.error('Error opening Sunday:', err)
  } finally {
    addingSunday.value = false
  }
}

onMounted(() => {
  fetchHolidays()
})
</script>

<style scoped>
.text-gold {
  color: #F6D993;
}
</style>
